import React from 'react';
import { X, Bot } from 'lucide-react';
import { Portal } from './Portal';

interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  onDontShowAgain?: () => void;
  title?: string;
  // Full Loom share URL or the loom id. We'll accept full URL for simplicity.
  loomUrl: string;
}

// Extracts the Loom video id from a share URL like
// https://www.loom.com/share/761bb7720a8a4bedbce203ca4142ed88?... -> 761bb7720a8a4bedbce203ca4142ed88
function getLoomId(loomUrl: string): string | null {
  try {
    const u = new URL(loomUrl);
    const parts = u.pathname.split('/').filter(Boolean);
    const idx = parts.findIndex((p) => p.toLowerCase() === 'share');
    if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
  } catch {
    // If not a full URL, assume it's already an ID
    if (loomUrl && /^[a-zA-Z0-9]+$/.test(loomUrl)) return loomUrl;
  }
  return null;
}

export const VideoModal: React.FC<VideoModalProps> = ({ open, onClose, onDontShowAgain, title = 'Quick tour', loomUrl }) => {
  if (!open) return null;
  const videoId = getLoomId(loomUrl);
  // Fallback: use original URL if parsing failed
  const embedSrc = videoId
    ? `https://www.loom.com/embed/${videoId}`
    : loomUrl.replace('/share/', '/embed/');

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
        {/* Dimmed backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        {/* Card */}
        <div className="relative w-full max-w-lg sm:max-w-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
          {/* Use the RagAgent-like gradient scoped to the card */}
          <div className="absolute inset-0 app-bg-card pointer-events-none" aria-hidden="true" />
          {/* Subtle dark veil for readability over the gradient */}
          <div className="absolute inset-0 bg-gray-950/50 pointer-events-none" aria-hidden="true" />
          <div className="relative text-white">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-white/10">
              <h3 className="text-base sm:text-lg font-semibold">{title}</h3>
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-1 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Video container - slightly smaller with glow ring and rounded corners */}
            <div className="px-4 sm:px-5 py-4">
              <div className="glow-ring-box rounded-xl w-[92%] sm:w-[88%] mx-auto">
                <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
                  <iframe
                    src={`${embedSrc}?autoplay=0`}
                    title="Loom video"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-4 sm:px-5 pb-5 flex flex-col items-center justify-center gap-2">
              <button
                onClick={() => {
                  try { onDontShowAgain && onDontShowAgain(); } catch {}
                  try { window.location.assign('/agent'); } catch {}
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9fffc] text-gray-900 hover:bg-white transition font-medium shadow-md"
              >
                <Bot className="w-4 h-4" />
                Chat with Agent
              </button>
              {onDontShowAgain && (
                <button
                  onClick={onDontShowAgain}
                  className="text-sm underline underline-offset-4 text-gray-300 hover:text-white"
                >
                  Don’t show again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};
